import { ShieldCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { cn } from '@/lib/utils';
import { api, type LifecycleState } from '../../api';
import type { Counts } from '../../api/areas/counts';
import { useSearchParams } from '../../lib/scopedParams';
import { useAccess } from '../../lib/access';
import { formatCount } from '../../lib/format';
import { useApi } from '../../lib/useApi';
import { findHub, hubUrl } from '../../nav/hubs';
import { FILTER_KEYS } from '../domains/DomainsPage';

type FilterId = 'all' | 'active' | 'expiring30' | 'expiring7' | 'expired' | 'grace' | 'redemption' | 'pendingDelete';

/** `days`: paid-until within the next N days (admin API `expires_within_days`). */
const FILTERS: { id: FilterId; state: LifecycleState | ''; days?: number; tone?: 'warn' | 'serious' | 'crit' }[] = [
  { id: 'all', state: '' },
  { id: 'active', state: 'ACTIVE' },
  { id: 'expiring30', state: 'ACTIVE', days: 30, tone: 'warn' },
  { id: 'expiring7', state: 'ACTIVE', days: 7, tone: 'warn' },
  { id: 'expired', state: 'EXPIRED', tone: 'serious' },
  { id: 'grace', state: 'GRACE_PERIOD', tone: 'serious' },
  { id: 'redemption', state: 'REDEMPTION', tone: 'crit' },
  { id: 'pendingDelete', state: 'PENDING_DELETE', tone: 'crit' },
];

const TONE_TEXT = { warn: 'text-warning', serious: 'text-serious', crit: 'text-critical' } as const;

/** How many domains a tile holds (exact, from one counts read; the list API has no total). */
function tileCount(counts: NonNullable<Counts['domains']>, f: (typeof FILTERS)[number]): string {
  if (f.days) return formatCount(f.days === 7 ? counts.expiring_within_7_days : counts.expiring_within_30_days);
  if (f.state) return formatCount(counts.by_lifecycle[f.state] ?? 0);
  return formatCount(Object.values(counts.by_lifecycle).reduce((a, b) => a + (b ?? 0), 0));
}

function Tile({ f, on, onPick, count }: { f: (typeof FILTERS)[number]; on: boolean; onPick: () => void; count: string | null }) {
  const { t } = useTranslation('nav');
  return (
    <button
      type="button"
      aria-pressed={on}
      onClick={onPick}
      className={cn(
        'flex min-w-0 flex-col items-start gap-1 rounded-xl border bg-card px-3.5 py-2.5 text-left transition-colors hover:border-primary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50',
        on && 'border-primary bg-accent',
      )}
    >
      <span className={cn('text-xs text-muted-foreground', on && 'text-accent-foreground')}>{t(`ui.domainFilter.${f.id}`)}</span>
      <span className={cn('tabular text-lg leading-none font-semibold', count && count !== '0' && f.tone ? TONE_TEXT[f.tone] : '')} data-tile-count={f.id}>
        {count ?? '…'}
      </span>
    </button>
  );
}

/**
 * Lifecycle tiles above the domain list (reference screens 02 and 05, "Gia hạn & vòng đời"):
 * each stage with how many domains are in it, one click to filter the list; "Bảo vệ" opens the
 * protected-domains tab (when the user may).
 */
export function DomainQuickFilters() {
  const { t } = useTranslation('nav');
  const [params, setParams] = useSearchParams();
  const access = useAccess();
  const counts = useApi((signal) => api.getCounts({ signal }), []);
  const domainCounts = counts.status === 'success' ? counts.data.domains : undefined;
  const home = findHub('home');
  const canSeeProtected = !!home && access.tabs(home).some((x) => x.id === 'protected');
  const current = params.get('lifecycle_state') ?? '';
  const currentDays = Number(params.get('expires_within_days') ?? '') || undefined;
  // A tile is a whole view whose count is its own: picking one drops the list's other filters, and
  // none is lit while another filter narrows the list below what the tiles count.
  const narrowed = FILTER_KEYS.some((k) => !['lifecycle_state', 'expires_within_days', 'expiry_basis'].includes(k) && params.get(k));
  const pick = (f: (typeof FILTERS)[number]) => {
    const next = new URLSearchParams(params);
    for (const k of [...FILTER_KEYS, 'after', 'page']) next.delete(k);
    if (f.state) next.set('lifecycle_state', f.state);
    if (f.days) next.set('expires_within_days', String(f.days));
    setParams(next);
  };
  const isOn = (f: (typeof FILTERS)[number]) => !narrowed && current === f.state && currentDays === f.days;
  return (
    <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-4 xl:grid-cols-9" role="group" aria-label={t('ui.domainFilter.label')}>
      {FILTERS.map((f) => (
        <Tile key={f.id} f={f} on={isOn(f)} onPick={() => pick(f)} count={counts.status === 'loading' ? null : domainCounts ? tileCount(domainCounts, f) : '—'} />
      ))}
      {canSeeProtected ? (
        <Link
          to={hubUrl('home', 'protected')}
          className="flex min-w-0 flex-col items-start gap-1 rounded-xl border border-dashed bg-card px-3.5 py-2.5 text-left transition-colors hover:border-primary/40"
        >
          <span className="text-xs text-muted-foreground">{t('ui.domainFilter.protected')}</span>
          <ShieldCheck className="size-4 text-muted-foreground" aria-hidden="true" />
        </Link>
      ) : null}
    </div>
  );
}
